"use client";

import { cn } from "@repo/ui/lib/utils";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui/components/ui/tabs";
import Link from "next/link";
import { IconButton } from "@repo/ui/components/ui/icon-button";
import { useTranslations } from "next-intl";
import { findCategoryPathById, useCategories } from "@/data/categories";
import { sanifyProductTitle } from "@repo/next-shared/utils";
import { Category } from "@/types/homepage";

export default function CategoriesTabs() {
  const { data: categories } = useCategories();
  const t = useTranslations();

  function getCategoryPath(id: string) {
    const path = findCategoryPathById(categories!, id);
    return path[path.length - 1]?.path ? path[path.length - 1]?.path : "";
  }

  return (
    <div className="w-full max-md:pb-[60px]">
      {categories && (
        <Tabs
          defaultValue={categories && categories[0] && categories[0].id}
          className="text-center max-md:text-left"
        >
          <div className="max-md:overflow-x-auto max-md:no-scrollbar">
            <TabsList variant="ghost">
              {categories &&
                categories
                  .filter((cat: Category) => cat.showOnHome)
                  .map((cat: Category) => (
                    <TabsTrigger
                      className="md:text-[18px]"
                      variant="ghost"
                      key={cat.id}
                      value={cat.id}
                    >
                      {cat.title}
                    </TabsTrigger>
                  ))}
            </TabsList>
          </div>
          {categories
            .filter((cat: Category) => cat.showOnHome)
            .map((cat: Category) => (
              <TabsContent key={cat.id} value={cat.id} className="">
                <div
                  className={cn(
                    "flex gap-[20px] justify-center max-md:justify-start overflow-x-auto max-md:no-scrollbar",
                    {
                      "justify-start":
                        cat &&
                        cat.children &&
                        cat.children.filter((child) => child.showOnHome)
                          .length > 4, // 5 = 4L1 + 1L0 showOnHome
                    },
                  )}
                >
                  <Link
                    href={`/categories/${getCategoryPath(cat.id)}/${sanifyProductTitle(cat.title)}`}
                    key={cat.id}
                  >
                    <div
                      className="rounded-[30px] h-[400px] w-[325px] max-2xl:w-[calc(20vw-35px)] max-lg:h-[320px] max-lg:w-[230px] flex justify-center items-center"
                      style={{
                        background: `url(${encodeURI(cat.imgUrl)})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                      }}
                    >
                      <button className="rounded-[30px] bg-primary-white px-[40px] max-2xl:px-[20px] py-[10px] mx-8 font-bold text-[20px] text-primary-black">
                        {cat.title}
                      </button>
                    </div>
                  </Link>
                  {cat &&
                    cat.children &&
                    cat.children
                      .filter((sub) => sub.showOnHome)
                      .map((sub) => (
                        <Link
                          href={`/categories/${getCategoryPath(sub.id)}/${sanifyProductTitle(sub.title)}`}
                          key={sub.id}
                        >
                          <div
                            className="rounded-[30px] h-[400px] w-[325px] max-2xl:w-[calc(20vw-35px)] max-lg:h-[320px] max-lg:w-[230px] flex justify-center items-center"
                            style={{
                              background: `url(${encodeURI(sub.imgUrl)})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "cover",
                            }}
                          >
                            <button className="rounded-[30px] bg-primary-white px-[40px] max-2xl:px-[20px] py-[10px] mx-8 font-bold text-[20px] text-primary-black">
                              {sub.title}
                            </button>
                          </div>
                        </Link>
                      ))}
                </div>
                <div>
                  <Link
                    className="mt-[20px] flex justify-end"
                    href={`/categories/${getCategoryPath(cat.id)}/${sanifyProductTitle(cat.title)}`}
                  >
                    <IconButton variant="underline" icon="ArrowCircle">
                      {t("view_all_of")}
                    </IconButton>
                  </Link>
                </div>
              </TabsContent>
            ))}
        </Tabs>
      )}
    </div>
  );
}
